import Objdict from "objdict"
import Zone from "@frog/source/models/Zone.js"
import Geometry from "@frog/source/utility/Geometry.js"

const BLOCK_SIZE = 1

export default class World {
    static parse({blocks, spots, encounters, edges, width, height}) {
        const blockXPositions = blocks.map((block) => block.position.x)
        const blockYPositions = blocks.map((block) => block.position.y)
        const world = {
            "width": width,
            "height":  height,

            "spots": spots || {},
            "zones": {},
            "edges": edges || {},
            "terrain": {},
            "blocks": {},
        }

        if(blocks.length > 0) {
            world.width = world.width ?? (Math.max(...blockYPositions) - Math.min(...blockYPositions) + 1)
            world.height = world.height ?? (Math.max(...blockXPositions) - Math.min(...blockXPositions) + 1)
        }

        encounters.forEach((encounter) => {
            const processedEncounter = processEncounterPolygon({
                "key": encounter.key,
                "zoomout": encounter.zoomout,
                "points": encounter.points,
                "encounterZ": encounter.z
            })

            if(processedEncounter.x2 > world.width) {
                world.width = processedEncounter.x2
            }
            if(processedEncounter.y2 > world.height) {
                world.height = processedEncounter.y2
            }
            world.zones[encounter.key] = processedEncounter
            return
        })
        blocks.forEach((block) => {
            block.position.xy = block.position.x + "x" + block.position.y
            block.position.xyz = block.position.xy + "x" + block.position.z
            const suffix = block.channelKey == undefined ? "" : "_" + block.channelKey

            world.blocks[block.position.xyz + suffix] = block
        })
        world.terrain = World.convertBlocksToTerrain(blocks)

        // If no terrain is defined beneath an encounter,
        // go ahead and generate some terrain for it.
        for(let x = 0; x < world.width; x += 1) {
            for(let y = 0; y < world.height; y += 1) {
                const xy = x + "x" + y
                const xyz = x + "x" + y + "x" + 0
                if(world.terrain[xy] == undefined) {
                    world.terrain[xy] = {
                        "position": {x, y, xy, "z": 0},
                        "size": {"x": 1, "y": 1, "z": 1},
                    }

                    world.blocks[xyz] = {
                        "position": {x, y, "z": 0, xy, xyz}
                    }
                }
            }
        }

        return world
    }
    static convertBlocksToTerrain(blocks) {
        const terrain = {}

        blocks.forEach((block) => {
            terrain[block.position.xy] = Objdict.merge(block, terrain[block.position.xy] || {})
            terrain[block.position.xy].position = {
                "x": block.position.x,
                "y": block.position.y,
                "xy": block.position.xy,
                "z": 0,
            }
            terrain[block.position.xy].size = terrain[block.position.xy].size || {"x": 1, "y": 1, "z": 0}
            if(terrain[block.position.xy].size.z < block.position.z + BLOCK_SIZE) {
                terrain[block.position.xy].size.z = block.position.z + BLOCK_SIZE
            }
        })

        return terrain
    }
    static getTerrain(world, position) {
        if(position == undefined) {
            return undefined
        }
        if(world.terrain[position.x + "x" + position.y] != undefined) {
            return world.terrain[position.x + "x" + position.y]
        }
        return {position, "size": {"x": 1, "y": 1, "z": 1}} // letting there be default out-of-zone terrain!!
        return undefined
    }
    static isOutOfBounds(world, position) {
        return position.x < 0
            || position.y < 0
            // || position.z < 0
            || position.x >= world.width
            || position.y >= world.height
            // || position.z >= world.depth
    }
    static isEmptyPosition(world, position) {
        return world.terrain[position.x + "x" + position.y] == undefined
    }
    static hasCollision({world, zone, previousPosition, nextPosition, position, options}) {
        previousPosition = previousPosition || position
        nextPosition = nextPosition || position

        options = options || {}
        options.isIgnoringLedges = options.isIgnoringLedges || false
        options.isIgnoringWallEdges = options.isIgnoringWallEdges || false
        options.isIgnoringCollision = options.isIgnoringCollision || false

        options.isIgnoringTerrain = true // DISABLED FOR NOW

        // TODO: DO YOU NEED TO ALSO PUT THIS COLLISION ELSEWHERE??
        if(options.isIgnoringLedges != true // TODO: Make a separate variable for this?
        && zone != undefined
        && options.isIgnoringZoneBoundaries != true
        && Zone.isInBounds(zone, nextPosition) == false) {
            return true
        }

        if(options.isIgnoringLedges != true
        && World.isOutOfBounds(world, nextPosition) == true) {
            return true
        }
        if(options.isIgnoringWallEdges == false
        && previousPosition != undefined
        && world.edges != undefined) {
            const direction2 = Geometry.vector(previousPosition, nextPosition)
            const direction1 = Geometry.flip(direction2)

            const key1 = previousPosition.x + "x" + previousPosition.y + "x" + previousPosition.z + "/" + (direction1.x > 0 ? "+" : "") + direction1.x + "x" + (direction1.y > 0 ? "+" : "") + direction1.y
            const key2 = nextPosition.x + "x" + nextPosition.y + "x" + nextPosition.z + "/" + (direction2.x > 0 ? "+" : "") + direction2.x + "x" + (direction2.y > 0 ? "+" : "") + direction2.y
            let edge1 = world.edges[key1]
            let edge2 = world.edges[key2]
            if(edge1 != undefined
            || edge2 != undefined) {
                return true
            }
        }
        const collidingSpot = Object.values(world.spots).find((spot) => spot.position.x == nextPosition.x && spot.position.y == nextPosition.y && spot.hasCollision)
        if(collidingSpot != undefined) {
            return true
        }
        const collisionBlock = world.blocks[nextPosition.x + "x" + nextPosition.y + "x" + nextPosition.z + "_collision"]
        if(options.isIgnoringCollsion != true
        && collisionBlock != undefined) {
            return true
        }
        const block = world.blocks[nextPosition.x + "x" + nextPosition.y + "x" + nextPosition.z]
        if(options.isIgnoringCollsion != true
        && block != undefined
        && block.hasCollision == true) {
            return true
        }
        const underfootCollisionBlock = world.blocks[nextPosition.x + "x" + nextPosition.y + "x" + (nextPosition.z - 1) + "_collision"]
        if(options.isIgnoringCollsion != true
        && underfootCollisionBlock != undefined) {
            return true
        }
        const terrain = world.terrain[nextPosition.x + "x" + nextPosition.y]
        // if(options.isIgnoringLedges != true // TODO: Make a separate variable for this??
        // && terrain == undefined) {
        //     return true
        // }
        if(options.isIgnoringCollsion != true
        && terrain != undefined
        && terrain.hasCollision == true) {
            return true
        }
        if(options.isIgnoringTerrain != true
        && terrain != undefined
        && nextPosition.z != terrain.position.z + terrain.size.z) {
            return true
            // const MOVEMENT_SIZE = 1
            // if(Math.abs(nextPosition.z - (terrain.position.z + terrain.size.z)) > MOVEMENT_SIZE) {
            //     return true
            // }
        }
        // if(previousPosition != undefined
        // && terrain.shape == "ramp") {
        //     if((terrain.direction == "east" || terrain.direction == "west")
        //     && previousPosition.y != nextPosition.y) {
        //         return true
        //     }
        //     if((terrain.direction == "south" || terrain.direction == "north")
        //     && previousPosition.x != nextPosition.x) {
        //         return true
        //     }
        // }
        return false
    }
}

function processEncounterPolygon({key, zoomout, points, encounterZ}) {
    const encounter = {
        "slug": key,
        "key": key,
        "zoomout": zoomout,
        "points": points
    }

    // We expect all points to be defined in clockwise
    // order, particularly for rendering the polygon.
    // If the encounter was defined counterclockwise,
    // go ahead and reverse the order of the points.
    // We can check if the encounter was defined in
    // counterclockwise order by computing the area.
    // If its area is negative, it is counterclockwise.

    encounter.area = Geometry.area(encounter.points)
    if(encounter.area < 0) encounter.points.reverse()

    encounter.points.forEach((point, index) => {
        const a = encounter.points[(index + 0) % encounter.points.length]
        const b = encounter.points[(index + 1) % encounter.points.length]
        const c = encounter.points[(index + 2) % encounter.points.length]
        b.internalAngle = Geometry.direction(a, c)
        b.internalAngle = Geometry.rotate(b.internalAngle, 90)
    })

    encounter.x1 = Math.min(...encounter.points.map((point) => point.x))
    encounter.x2 = Math.max(...encounter.points.map((point) => point.x))
    encounter.y1 = Math.min(...encounter.points.map((point) => point.y))
    encounter.y2 = Math.max(...encounter.points.map((point) => point.y))
    encounter.x = encounter.x1
    encounter.y = encounter.y1
    encounter.width = encounter.x2 - encounter.x1
    encounter.height = encounter.y2 - encounter.y1
    encounter.center = {
        "x": encounter.x + ((encounter.x2 - encounter.x1) / 2) + 0.5,
        "y": encounter.y + ((encounter.y2 - encounter.y1) / 2) + 0.5
    }
    encounter.zone = encounter

    encounter.z = encounterZ

    return encounter
}
